import React, { useState } from 'react';
import {
  Box, Typography, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, IconButton, TextField, Button,
  FormControl, InputLabel, Select, MenuItem, Avatar, Chip,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { laborStatusColors, getAvatarColor, surfaces } from '../../theme/tokens';
import { parseTime, formatTime } from '../../utils/timeUtils';

const STATUS_CONFIG = {
  hold: { ...laborStatusColors.hold },
  pending: { ...laborStatusColors.pending },
  in_progress: { ...laborStatusColors.in_progress },
  complete: { ...laborStatusColors.complete },
};

export const PendingLaborSection = ({ entries, onChange, members, currentUserId }) => {
  const [showAdd, setShowAdd] = useState(false);
  const hasActiveEntries = entries.some((e) => e.status === 'pending' || e.status === 'in_progress');
  const defaultStatus = hasActiveEntries ? 'hold' : 'pending';
  const [addForm, setAddForm] = useState({ user: currentUserId || '', status: defaultStatus, time: '', note: '' });

  const handleAdd = () => {
    if (!addForm.user) return;
    const entry = {
      _tempId: Date.now(),
      user: addForm.user,
      status: addForm.status,
      order: entries.length,
      time: addForm.time ? parseTime(addForm.time) : undefined,
      note: addForm.note || undefined,
      _userName: (members || []).find((m) => m._id === addForm.user)?.name || '',
    };
    onChange([...entries, entry]);
    const nextDefault = (entries.length > 0 || entry.status === 'pending' || entry.status === 'in_progress') ? 'hold' : 'pending';
    setAddForm({ user: currentUserId || '', status: nextDefault, time: '', note: '' });
    setShowAdd(false);
  };

  const handleUpdate = (tempId, field, value) => {
    onChange(entries.map((e) => e._tempId === tempId ? { ...e, [field]: value } : e));
  };

  const handleDragEnd = (result) => {
    if (!result.destination) return;
    const srcIdx = result.source.index;
    const destIdx = result.destination.index;
    if (srcIdx === destIdx) return;

    const reordered = Array.from(entries);
    const [moved] = reordered.splice(srcIdx, 1);
    reordered.splice(destIdx, 0, moved);
    onChange(reordered.map((e, idx) => ({ ...e, order: idx })));
  };

  return (
    <Box sx={{ mt: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'text.secondary' }}>
          Work Items ({entries.length})
        </Typography>
        <Button size="small" startIcon={<AddIcon />} onClick={() => setShowAdd(!showAdd)}
          sx={{ textTransform: 'none', fontSize: 12, py: 0 }}>Add</Button>
      </Box>

      {showAdd && (
        <Box sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'center', flexWrap: 'wrap' }}>
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>User *</InputLabel>
            <Select value={addForm.user} onChange={(e) => setAddForm({ ...addForm, user: e.target.value })} label="User *">
              {(members || []).map((m) => (
                <MenuItem key={m._id} value={m._id}>{m.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Status</InputLabel>
            <Select value={addForm.status} onChange={(e) => setAddForm({ ...addForm, status: e.target.value })} label="Status">
              <MenuItem value="hold">Hold</MenuItem>
              <MenuItem value="pending">Pending</MenuItem>
              <MenuItem value="in_progress">In Progress</MenuItem>
              <MenuItem value="complete">Complete</MenuItem>
            </Select>
          </FormControl>
          <TextField size="small" label="Time" value={addForm.time} placeholder="e.g. 1h 30m"
            onChange={(e) => setAddForm({ ...addForm, time: e.target.value })}
            sx={{ width: 90 }} inputProps={{ min: 0, step: 0.25 }} />
          <TextField size="small" label="Note" value={addForm.note}
            onChange={(e) => setAddForm({ ...addForm, note: e.target.value })}
            sx={{ flex: 1, minWidth: 120 }} />
          <Button variant="contained" size="small" onClick={handleAdd} disabled={!addForm.user}
            sx={{ textTransform: 'none', fontWeight: 600, minWidth: 60 }}>Add</Button>
          <Button size="small" onClick={() => setShowAdd(false)} sx={{ textTransform: 'none', minWidth: 50 }}>Cancel</Button>
        </Box>
      )}

      {entries.length > 0 && (
        <DragDropContext onDragEnd={handleDragEnd}>
          <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: surfaces.elevated }}>
                  <TableCell sx={{ py: 0.75, width: 30 }} />
                  <TableCell sx={{ fontWeight: 600, color: 'text.secondary', py: 0.75 }}>Order</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: 'text.secondary', py: 0.75 }}>User</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: 'text.secondary', py: 0.75 }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: 'text.secondary', py: 0.75 }}>Time</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: 'text.secondary', py: 0.75 }}>Note</TableCell>
                  <TableCell sx={{ py: 0.75 }} align="right" />
                </TableRow>
              </TableHead>
              <Droppable droppableId="pending-labor-list">
                {(provided) => (
                  <TableBody ref={provided.innerRef} {...provided.droppableProps}>
                    {entries.map((entry, index) => {
                      const statusCfg = STATUS_CONFIG[entry.status] || STATUS_CONFIG.pending;
                      const draggableId = String(entry._tempId);
                      return (
                        <Draggable key={draggableId} draggableId={draggableId} index={index}>
                          {(provided, snapshot) => (
                            <TableRow
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              sx={{
                                '& td': { py: 0.5 },
                                backgroundColor: snapshot.isDragging ? surfaces.subtle : 'inherit',
                                ...(entry.status === 'hold' && { opacity: 0.6 }),
                              }}
                            >
                              <TableCell sx={{ width: 30, px: 0.5 }}>
                                <Box {...provided.dragHandleProps} sx={{ display: 'flex', alignItems: 'center', cursor: 'grab' }}>
                                  <DragIndicatorIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
                                </Box>
                              </TableCell>
                              <TableCell>
                                <Typography variant="caption" sx={{ color: 'text.disabled', fontWeight: 600, fontSize: 11 }}>
                                  #{index + 1}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                  <Avatar sx={{ width: 22, height: 22, fontSize: 10, bgcolor: getAvatarColor(entry.user) }}>
                                    {entry._userName?.[0]?.toUpperCase() || '?'}
                                  </Avatar>
                                  <Typography variant="caption" sx={{ fontWeight: 500 }}>{entry._userName || 'Me'}</Typography>
                                </Box>
                              </TableCell>
                              <TableCell>
                                <Select
                                  value={entry.status || 'pending'}
                                  onChange={(e) => handleUpdate(entry._tempId, 'status', e.target.value)}
                                  size="small" variant="standard" disableUnderline sx={{ fontSize: 12 }}
                                  renderValue={(val) => (
                                    <Chip label={STATUS_CONFIG[val]?.label || val} size="small"
                                      sx={{ height: 20, fontSize: 10, fontWeight: 600, backgroundColor: STATUS_CONFIG[val]?.bg, color: STATUS_CONFIG[val]?.color, cursor: 'pointer' }} />
                                  )}
                                >
                                  <MenuItem value="hold">Hold</MenuItem>
                                  <MenuItem value="pending">Pending</MenuItem>
                                  <MenuItem value="in_progress">In Progress</MenuItem>
                                  <MenuItem value="complete">Complete</MenuItem>
                                </Select>
                              </TableCell>
                              <TableCell>
                                <TextField size="small" variant="standard" type="number"
                                  value={entry.time || ''} placeholder="—"
                                  onChange={(e) => handleUpdate(entry._tempId, 'time', e.target.value ? parseTime(e.target.value) : undefined)}
                                  inputProps={{ min: 0, step: 0.25, style: { fontSize: 12, width: 50 } }} />
                              </TableCell>
                              <TableCell>
                                <TextField size="small" variant="standard"
                                  value={entry.note || ''} placeholder="Add note..."
                                  onChange={(e) => handleUpdate(entry._tempId, 'note', e.target.value)}
                                  inputProps={{ style: { fontSize: 12 } }} sx={{ minWidth: 100 }} />
                              </TableCell>
                              <TableCell align="right">
                                <IconButton size="small" onClick={() => onChange(entries.filter((e) => e._tempId !== entry._tempId))} sx={{ color: 'error.main', p: 0.25 }}>
                                  <DeleteIcon sx={{ fontSize: 16 }} />
                                </IconButton>
                              </TableCell>
                            </TableRow>
                          )}
                        </Draggable>
                      );
                    })}
                    {provided.placeholder}
                  </TableBody>
                )}
              </Droppable>
            </Table>
          </TableContainer>
        </DragDropContext>
      )}
    </Box>
  );
};
